import { useEffect, useState } from "react";
import { Eye, EyeOff, Star, Trash2, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { name: "", service: "", rating: 5, text: "" };

export function Stars({ value, onChange, size = 16 }) {
  return (
    <div className={onChange ? "stars interactive" : "stars"} data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} disabled={!onChange} onClick={() => onChange && onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`star-${n}`}>
          <Star size={size} fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export function TestimonialsManager() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/testimonials", form);
      setItems((list) => [data, ...(list || [])]);
      setForm(EMPTY);
      toast.success("Review added — it's live on the website");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    try {
      const { data } = await api.patch(`/admin/testimonials/${t.id}`, { visible: !t.visible });
      setItems((list) => list.map((x) => (x.id === t.id ? data : x)));
      toast.success(data.visible ? "Review is now visible" : "Review hidden from website");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setItems((list) => list.filter((x) => x.id !== id));
      toast.success("Review deleted");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <section data-testid="testimonials-manager">
      <header className="admin-head"><div><p className="eyebrow light">KIND WORDS FROM CLIENTS</p><h1>Reviews</h1></div><p className="muted">Add what your clients say about you. Visible reviews appear on the website.</p></header>
      <form className="upload-card review-form" onSubmit={submit} data-testid="testimonial-form">
        <div className="upload-fields">
          <label>Client name<input name="name" value={form.name} onChange={update} placeholder="e.g. Priya Deshmukh" required maxLength={80} data-testid="testimonial-name-input" /></label>
          <label>Service<input name="service" value={form.service} onChange={update} placeholder="e.g. Bridal Makeup" maxLength={80} data-testid="testimonial-service-input" /></label>
          <label>Rating<Stars value={form.rating} onChange={(rating) => setForm({ ...form, rating })} size={20} /></label>
        </div>
        <div className="upload-fields">
          <label>Review<textarea name="text" value={form.text} onChange={update} placeholder="What did they love about their experience?" required maxLength={600} rows={5} data-testid="testimonial-text-input" /></label>
          <button className="gold-button" type="submit" disabled={busy} data-testid="testimonial-submit"><Quote size={15} /> {busy ? "Saving…" : "Publish review"}</button>
        </div>
      </form>
      {items === null ? <p className="muted">Loading reviews…</p> : items.length === 0 ? <p className="muted empty" data-testid="testimonials-empty">No reviews yet — add your first one above.</p> : (
        <div className="review-list" data-testid="admin-review-list">{items.map((t) => (
          <article key={t.id} className={t.visible ? "review-card" : "review-card hidden"} data-testid={`review-card-${t.id}`}>
            <Stars value={t.rating} size={13} />
            <p className="review-text">“{t.text}”</p>
            <footer><div><strong data-testid="review-name">{t.name}</strong>{t.service && <span>{t.service}</span>}</div>
              <div className="review-actions">
                <button onClick={() => toggle(t)} aria-label={t.visible ? "Hide review" : "Show review"} title={t.visible ? "Visible on website" : "Hidden"} data-testid="review-toggle-button">{t.visible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                <button onClick={() => remove(t.id)} className="danger" aria-label="Delete review" data-testid="review-delete-button"><Trash2 size={15} /></button>
              </div></footer>
          </article>
        ))}</div>
      )}
    </section>
  );
}
